import { ApplicationBaseMessage, ApplicationActionTypes } from '@numeo/types-main-web-app'
import { WsMessageCategory } from '@numeo/types-core'
import type { ClientApplication } from '@numeo/types-core'
import { sanitizeApplication } from 'hooks/auth/utils/user-sanitization'
import { AppDispatch, RootState } from 'store'
import { patchApplication, selectApplication, selectUser, setApplication } from 'store/authSlice'
import { log } from 'utils/logger'

/**
 * Identity check for "is this push about the company this tab is showing". Compares `id` when both
 * sides carry one and falls back to `projectName`, because the two ClientApplication code paths
 * differ: the single-object mapper sets `id`, the array mapper mutates in place and drops `_id`.
 * A payload that carries neither identifier is treated as a match — it cannot be attributed to
 * another company, so dropping it would silently lose a legitimate update.
 */
const isSameApplication = (current: ClientApplication, incoming: ClientApplication): boolean => {
    if (current.id && incoming.id) return String(current.id) === String(incoming.id)
    if (current.projectName && incoming.projectName) return current.projectName === incoming.projectName
    return true
}

export const handleApplicationMessages = (message: ApplicationBaseMessage, dispatch: AppDispatch, getState: () => RootState): boolean => {
    if (message.categoryType !== WsMessageCategory.Application) return false

    try {
        switch (message.data.action) {
            case ApplicationActionTypes.APP_ApplicationUpdated:
            case ApplicationActionTypes.APP_GetApplication: {
                const state = getState()
                const currentUser = selectUser(state)

                // Sanitize application before updating Redux store
                const sanitizedApplication = sanitizeApplication(message.data.application, currentUser)

                const existingApplication = selectApplication(state)

                // Multi-MC: this push may belong to a DIFFERENT company than the one this tab is
                // showing. `WebSocketManager.sendToUsers` scopes by projectName, but `resolveProjectName`
                // downgrades that to `undefined` whenever the `per-tab-company-switch` Flipt flag is off
                // for the company — and socket-service treats an undefined projectName as a fan-out to
                // EVERY SPA connection the user has open (server.ts `sendMessageToClient`). So a user with
                // MC A and MC B open gets MC A's application pushed into the MC B tab, and applying it
                // swaps the active company underneath them: the sidebar flips to MC A, and every Ongoing
                // Loads row loses its truck (the rows are MC B loads, `temp.spotFinder.trucks` is now MC A's),
                // so the assignment cell falls back to "Assign truck" and the ETA chip disappears. A real
                // company switch never arrives this way — CompanySwitcher hard-navigates and re-runs
                // /auth/verify — so a push for another company is only ever cross-talk. Drop it.
                if (sanitizedApplication && existingApplication && !isSameApplication(existingApplication, sanitizedApplication)) {
                    log.w('Ignored an application socket push addressed to another company', {
                        action: message.data.action,
                        activeProjectName: existingApplication.projectName,
                        pushedProjectName: sanitizedApplication.projectName,
                    })
                    return true
                }

                if (sanitizedApplication) {
                    // The socket payload carries the raw embedded application.subscription, which lacks the
                    // derived TMS overlay (featureFlags.TMS, entitlements, the tms block) that only /auth/verify
                    // computes from the canonical org SubscriptionRepo (ADR-020). Overwriting the authoritative
                    // subscription with this degraded copy makes FeatureGate falsely show "Your trial has ended"
                    // until the next verifyAuth (e.g. a page refresh). These application updates are never the
                    // billing channel — they fire on telegram/ELD/load-status changes — so keep the subscription
                    // already hydrated from /auth/verify instead of letting the socket downgrade it.
                    const existingSubscription = existingApplication?.subscription
                    if (existingSubscription) {
                        sanitizedApplication.subscription = existingSubscription
                    }

                    // A payload with no `temp` at all empties `temp.spotFinder.trucks`, so every load's
                    // truck lookup misses and the ETA chip, assignment names and ELD badges vanish from
                    // Ongoing Loads until the next /auth/verify. Deliberately NOT patched over by keeping
                    // the previously hydrated block: both actions here can carry a truck deletion or an ELD
                    // disconnect, so there is no action safe to whitelist, and substituting stale trucks
                    // would turn a loud, immediately-reported failure into a dispatcher acting on a truck
                    // that no longer exists. Warn instead, so the emitter gets named the next time it fires.
                    if (!sanitizedApplication.temp && existingApplication?.temp) {
                        log.w('Application socket push arrived without `temp` — truck-derived UI will be empty until the next /auth/verify', {
                            action: message.data.action,
                            projectName: sanitizedApplication.projectName,
                        })
                    }
                }

                // Update application in Redux store
                dispatch(setApplication(sanitizedApplication))
                break
            }
            case ApplicationActionTypes.APP_ApplicationPatched: {
                const existingApplication = selectApplication(getState())
                // Nothing to patch yet: /auth/verify brings the whole application, changes included.
                if (!existingApplication) return true
                // Same cross-company guard as the full push above.
                if (existingApplication.projectName !== message.data.projectName) {
                    log.w('Ignored an application patch addressed to another company', {
                        activeProjectName: existingApplication.projectName,
                        pushedProjectName: message.data.projectName,
                    })
                    return true
                }
                dispatch(patchApplication(message.data.ops))
                break
            }
            default:
                return false
        }

        return true
    } catch (error) {
        log.e('Error processing application message:', error)
        return false
    }
}
